import {useState, useEffect, useMemo, useCallback} from "react";
import {useQuery, useMutation} from "@tanstack/react-query";
import { bumpSpaceInCache, bumpThreadInSummariesCache } from "@/lib/sidebarRecencyCache";
import {
    Layers,

    ListTodo,
    MessageSquare,
    Plus,
    Brain,
    Sparkles,
    Calendar,
    Cpu,
    CheckSquare,
    BarChart3,
    FolderOpen,
    PanelLeft,
    Users,
    Tag,
    ChevronDown,
    ChevronRight,
    Crown,
    Zap,
    Network,
    Download,
    LayoutDashboard,
    Home,
    Bell,
    Lightbulb,
    TrendingUp,
    BookOpen,
    Target,
    PlayCircle,
    Trash2,
    Archive,
    ArchiveRestore,
    MoreHorizontal,
    Share2,
    Lock,
    Youtube,
    Pin,
    PinOff,
    Pencil,
    Link2,
    X,
    ImageIcon, Wand2, Bot, Mic, MessageCircle, Building2, Puzzle, Workflow, KeyRound, BookMarked, BadgePercent, Library
} from "lucide-react";
import { SiGoogledrive } from "react-icons/si";
import { usePluginsOptional } from "@/plugins/PluginContext";
import type { PluginSidebarItem } from "@/plugins/types";
import {queryClient, apiRequest} from "@/lib/queryClient";
import {useToast} from "@/hooks/use-toast";
import { ragService } from "@/services/ragService";
import { EmbeddedSearchMark } from "@/components/EmbeddedSearchMark";
import {DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger} from "@/components/ui/dropdown-menu";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
} from "@/components/ui/alert-dialog";
import {Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter} from "@/components/ui/dialog";
import {Input} from "@/components/ui/input";
import {Label} from "@/components/ui/label";
import {ThreadLinkingDialog} from "@/components/ThreadLinkingDialog";
import {SidebarUniversalSearch} from "@/components/SidebarUniversalSearch";
import {UserMenu} from "@/components/UserMenu";
import {
    Sidebar,
    SidebarContent,
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarHeader,
    SidebarFooter,
    useSidebar,
} from "@/components/ui/sidebar";
import {Link, useLocation} from "wouter";
import {Button} from "@/components/ui/button";
import {Tooltip, TooltipContent, TooltipTrigger} from "@/components/ui/tooltip";
import {Collapsible, CollapsibleContent} from "@/components/ui/collapsible";
import {Badge} from "@/components/ui/badge";
import {useMastermind} from "@/contexts/MastermindContext";
import {InstallAppButton} from "@/components/InstallAppButton";
import type {ChatThread, MastermindSpace, Topic} from "@shared/schema";
import {getTopicIcon} from "@/lib/utils";
import {PinVerificationDialog} from "@/components/PinVerificationDialog";
import {DropdownMenuSeparator} from "@/components/ui/dropdown-menu";
import {chatThreadsService} from "@/services/chatThreadsService.ts";
import {sourcesService} from "@/services/sourcesService";
import { useKnowledgeBaseList } from "@/contexts/KnowledgeBaseContext";
import {mastermindSpacesService} from "@/services/mastermindSpacesService";
import {useAuth} from "@/contexts/AuthContext.tsx";
import {useWorkspace} from "@/contexts/WorkspaceContext";
import {useOnboarding} from "@/contexts/OnboardingContext";
import { creditsService, type CreditsOverview } from "@/services/creditsService";
import { workspacesService } from "@/services/workspacesService";

// Essential tools - most commonly used
const essentialToolsItems = [
    {
        title: "Creative Studio",
        url: "/creative-studio",
        icon: ImageIcon,
    },
    {
        title: "Smart Tasks",
        url: "/tasks",
        icon: ListTodo,
    },
    {
        title: "Compare Models",
        url: "/compare-models",
        icon: Layers,
    },
    {
        title: "YouTube Automation",
        url: "/youtube",
        icon: Youtube,
    },
    {
        title: "AI Agents",
        url: "/ai-agents",
        icon: Bot,
    },
    {
        title: "Coding Standards",
        url: "/coding-standards",
        icon: BookMarked,
    },
];

// Advanced features - hidden by default for simpler UX
const advancedItems = [
    {
        title: "Command Center",
        url: "/command-center",
        icon: LayoutDashboard,
    },
    {
        title: "Playbook Builder",
        url: "/playbooks",
        icon: BookOpen,
    },
    {
        title: "Automations",
        url: "/automations",
        icon: Zap,
    },
    {
        title: "Agent Missions",
        url: "/missions",
        icon: Target,
    },
    {
        title: "Performance",
        url: "/performance",
        icon: PlayCircle,
    },
    {
        title: "Reminders",
        url: "/reminders",
        icon: Bell,
    },
    {
        title: "Workflow Canvas",
        url: "/workflow-canvas",
        icon: Workflow,
    },
];

// Settings & utility items
const utilityItems = [
    {
        title: "Daily Digest",
        url: "/daily-digest",
        icon: Calendar,
    },
    {
        title: "Workspaces",
        url: "/workspaces",
        icon: Layers,
    },
    {
        title: "Tag Management",
        url: "/tags",
        icon: Tag,
    },
    {
        title: "Export & Import",
        url: "/export-import",
        icon: Download,
    },
    {
        title: "Developer API",
        url: "/developer-api",
        icon: KeyRound,
    },
    {
        title: "Connect AI tools",
        url: "/mcp-connections",
        icon: Bot,
    },
    {
        title: "Token savings",
        url: "/token-savings",
        icon: BadgePercent,
    },
    {
        title: "Shelf library",
        url: "/shelf-library",
        icon: Library,
    },
];

const communicationItems = [
    {
        title: "GoHighLevel",
        url: "/communication/gohighlevel",
        icon: Building2,
    },
    {
        title: "Fireflies",
        url: "/fireflies",
        icon: Mic,
    },
    {
        title: "Google Drive",
        url: "/communication/google-drive",
        icon: SiGoogledrive,
    },
    {
        title: "Telegram",
        url: "/communication/telegram",
        icon: MessageCircle,
    },
];

const bottomItems: { title: string; url: string; icon: any }[] = [];

/** Sidebar icon for a plugin-contributed entry: plugin icon file or a Puzzle fallback. */
function makePluginItemIcon(iconUrl: string | null) {
    return function PluginItemIcon({ className }: { className?: string }) {
        if (!iconUrl) return <Puzzle className={className} />;
        return <img src={iconUrl} alt="" className={className} />;
    };
}

function pluginItemsToMenuItems(items: PluginSidebarItem[] | undefined) {
    return (items ?? []).map((item) => ({
        title: item.title,
        url: item.url,
        icon: makePluginItemIcon(item.iconUrl),
    }));
}

// All tools combined for lookup by URL (incl. video admin tool for pinning)
const allToolItems = [...essentialToolsItems, ...advancedItems, ...utilityItems];
const toolsByUrl = Object.fromEntries(allToolItems.map(item => [item.url, item]));

interface UserCredits {
    id?: string;
    userId?: string;
    balance: string;
    totalEarned: string;
    totalSpent: string;
    lastResetAt?: string | null;
    createdAt?: string;
    updatedAt?: string;
    subscription?: {
        id: string;
        planId: string;
        status: string;
        plan?: SubscriptionPlan;
    } | null;
}

interface SubscriptionPlan {
    id: string;
    name: string;
    displayName: string;
    monthlyCredits: string;
    price: string;
    features: any;
}

// Exception emails that should have Library and Tools unlocked on first login
const EXCEPTION_EMAILS = [
    "yanose9753@gavrom.com"
    // Add emails here that should bypass the onboarding lock
    // Example: 'admin@example.com', 'test@example.com'
];

export function AppSidebar() {
    const [location, setLocation] = useLocation();
    const {toggleSidebar, state: sidebarState} = useSidebar();
    const isSidebarCollapsed = sidebarState === "collapsed";
    const {toast} = useToast();
    const {user} = useAuth();
    const {currentWorkspaceId, setCurrentWorkspaceId, workspaces, isLoadingWorkspaces, isWorkspaceContext, refetchWorkspaces} = useWorkspace();
    const [createWorkspaceOpen, setCreateWorkspaceOpen] = useState(false);
    const [newWorkspaceName, setNewWorkspaceName] = useState("");
    const { isFeatureUnlocked: originalIsFeatureUnlocked } = useOnboarding();
    
    // Check if user email is in exception list
    const isExceptionUser = user?.email && EXCEPTION_EMAILS.includes(user.email.toLowerCase());
    // Override feature unlock check for exception users
    const isFeatureUnlocked = (feature: 'dashboard' | 'library' | 'tools' | 'advanced') => {
        // For exception users, always unlock 'library' and 'tools'
        if (isExceptionUser && (feature === 'library' || feature === 'tools')) {
            return true;
        }
        // Otherwise use the original logic
        return originalIsFeatureUnlocked(feature);
    };
    const [toolsOpen, setToolsOpen] = useState(false);
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [utilityOpen, setUtilityOpen] = useState(false);

    // Plugin-contributed sidebar entries (merged into the static section arrays below)
    const plugins = usePluginsOptional();
    const pluginSidebar = plugins?.sidebarBySection;
    const pluginRootSections = plugins?.rootSections ?? [];
    const [pluginSectionsOpen, setPluginSectionsOpen] = useState<Record<string, boolean>>({});
    const mergedCommunicationItems = useMemo(
        () => [...communicationItems, ...pluginItemsToMenuItems(pluginSidebar?.communications)],
        [pluginSidebar]
    );
    const mergedEssentialToolsItems = useMemo(
        () => [...essentialToolsItems, ...pluginItemsToMenuItems(pluginSidebar?.["essential-tools"])],
        [pluginSidebar]
    );
    const mergedAdvancedItems = useMemo(
        () => [...advancedItems, ...pluginItemsToMenuItems(pluginSidebar?.advanced)],
        [pluginSidebar]
    );
    const mergedUtilityItems = useMemo(
        () => [...utilityItems, ...pluginItemsToMenuItems(pluginSidebar?.utilities)],
        [pluginSidebar]
    );

    useEffect(() => {
        if (location.startsWith("/fireflies") || location.startsWith("/communication/")) {
            setCommunicationOpen(true);
        }
    }, [location]);

    const [recentChatsOpen, setRecentChatsOpen] = useState(false);
    const [archivedChatsOpen, setArchivedChatsOpen] = useState(false);
    const [spacesOpen, setSpacesOpen] = useState(false);
    const [communicationOpen, setCommunicationOpen] = useState(false);
    const [proMode, setProMode] = useState(() => {
        if (typeof window !== "undefined") {
            return localStorage.getItem("pro_mode") === "true";
        }
        return false;
    });

    // Listen for Pro Mode changes from Settings
    useEffect(() => {
        const handleProModeChange = (event: CustomEvent<boolean>) => {
            setProMode(event.detail);
        };
        window.addEventListener("proModeChange", handleProModeChange as EventListener);
        return () => {
            window.removeEventListener("proModeChange", handleProModeChange as EventListener);
        };
    }, []);
    // Thread state types - use partial thread type that works with ThreadSummary
    type ThreadRef = { id: string; title: string | null; isLocked?: boolean };
    type ThreadLinkRef = { id: string; title: string | null; linkedSpaceIds: string[] | null; topicId: string | null; tags: string[] | null };
    type ThreadSummary = Omit<ChatThread, 'messages'> & { messageCount: number };
    const [threadToDelete, setThreadToDelete] = useState<ThreadRef | null>(null);
    const [isPinDialogOpen, setIsPinDialogOpen] = useState(false);
    const [threadToLock, setThreadToLock] = useState<ThreadRef | null>(null);
    const [pendingThreadIdToOpen, setPendingThreadIdToOpen] = useState<string | null>(null);
    const [threadToRename, setThreadToRename] = useState<ThreadRef | null>(null);
    const [renameValue, setRenameValue] = useState("");
    const [threadToLink, setThreadToLink] = useState<ThreadLinkRef | null>(null);

    // Get active thread ID from context for reactive highlighting
    const {currentThreadId: activeThreadId, setCurrentThreadId} = useMastermind();

    const deleteThreadMutation = useMutation({
        mutationFn: async (threadId: string) => {
            await chatThreadsService.deleteChatThread(threadId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['/api/chat-threads']});
            queryClient.invalidateQueries({ queryKey: ["accessible-thread-summaries"] });
            toast({
                title: "Thread deleted",
                description: "The chat thread has been moved to trash for 30 days.",
            });
            if (activeThreadId === threadToDelete?.id) {
                setCurrentThreadId(null);
                setLocation("/");
            }
            setThreadToDelete(null);
        },
        onError: () => {
            toast({
                title: "Error",
                description: "Failed to delete thread. Please try again.",
                variant: "destructive",
            });
        },
    });

    // PIN status query
    const {data: pinStatus} = useQuery<{ hasPinSet: boolean; isSessionValid: boolean }>({
        queryKey: ["/api/pin/status"],
        queryFn: async () => {
            const { pinService } = await import("@/services/pinService");
            return pinService.getStatus();
        },
    });

    // Toggle thread lock mutation
    const toggleLockMutation = useMutation({
        mutationFn: async ({threadId, lock}: { threadId: string; lock: boolean }) => {
            const result = await chatThreadsService.lockChatThread(threadId, lock, currentWorkspaceId ?? null);
            if (!result) {
                throw new Error("Failed to toggle lock");
            }
            return result;
        },
        onSuccess: (_, { lock }) => {
            queryClient.invalidateQueries({ queryKey: ["/api/chat-threads"] });
            queryClient.invalidateQueries({ queryKey: ["/api/chat-thread-summaries", user?.id] });
            toast({
                title: lock ? "Thread Locked" : "Thread Unlocked",
                description: lock ? "This thread is now protected with your PIN" : "Thread is now accessible",
            });
            setThreadToLock(null);
        },
        onError: (error: Error) => {
            toast({
                title: "Error",
                description: error.message,
                variant: "destructive",
            });
        },
    });

    // Rename thread mutation
    const renameMutation = useMutation({
        mutationFn: async ({threadId, title}: { threadId: string; title: string }) => {
            const result = await chatThreadsService.updateChatThread(threadId, {title}, currentWorkspaceId ?? null);
            if (!result) {
                throw new Error("Failed to rename thread");
            }
            return result;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["/api/chat-threads"]});
            queryClient.invalidateQueries({queryKey: ['/api/chat-thread-summaries', user?.id, currentWorkspaceId ?? 'personal']});
            toast({
                title: "Thread renamed",
                description: "The thread title has been updated.",
            });
            setThreadToRename(null);
            setRenameValue("");
        },
        onError: (error: Error) => {
            toast({
                title: "Rename failed",
                description: error.message,
                variant: "destructive",
            });
        },
    });

    // Archive/unarchive thread mutation (WhatsApp-style)
    const archiveThreadMutation = useMutation({
        mutationFn: async ({threadId, archived}: { threadId: string; archived: boolean }) => {
            const result = await chatThreadsService.updateChatThread(threadId, {
                status: archived ? "archived" : "active",
            }, currentWorkspaceId ?? null);
            if (!result) {
                throw new Error("Failed to update archive status");
            }
            return result;
        },
        onSuccess: (_, { archived }) => {
            queryClient.invalidateQueries({queryKey: ['/api/chat-thread-summaries', user?.id, currentWorkspaceId ?? 'personal']});
            queryClient.invalidateQueries({queryKey: ['/api/chat-threads']});
            toast({
                title: archived ? "Thread archived" : "Thread unarchived",
                description: archived ? "Moved to Archived chats." : "Moved back to Recent chats.",
            });
        },
        onError: (error: Error) => {
            toast({
                title: "Archive failed",
                description: error.message,
                variant: "destructive",
            });
        },
    });

    // Update linked spaces mutation
    const updateSpacesMutation = useMutation({
        mutationFn: async ({threadId, spaceIds}: { threadId: string; spaceIds: string[] }) => {
            const result = await chatThreadsService.updateChatThread(threadId, {linkedSpaceIds: spaceIds}, currentWorkspaceId ?? null);
            if (!result) {
                throw new Error("Failed to update spaces");
            }
            return result;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["/api/chat-threads"]});
        },
    });

    // Update topic mutation
    const updateTopicMutation = useMutation({
        mutationFn: async ({threadId, topicId}: { threadId: string; topicId: string | null }) => {
            const result = await chatThreadsService.updateChatThread(threadId, {topicId}, currentWorkspaceId ?? null);
            if (!result) {
                throw new Error("Failed to update topic");
            }
            return result;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["/api/chat-threads"]});
        },
    });

    // Update tags mutation
    const updateTagsMutation = useMutation({
        mutationFn: async ({threadId, tags}: { threadId: string; tags: string[] }) => {
            const result = await chatThreadsService.updateChatThread(threadId, {tags}, currentWorkspaceId ?? null);
            if (!result) {
                throw new Error("Failed to update tags");
            }
            return result;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["/api/chat-threads"]});
        },
    });

    // Handler for lock button click (reserved for future use)
    const _handleLockClick = (thread: ThreadSummary) => {
        // Check if PIN is set
        if (!pinStatus?.hasPinSet) {
            toast({
                title: "PIN Required",
                description: "Please set a security PIN in Settings first",
            });
            setLocation("/settings");
            return;
        }

        // Check if session is valid
        if (!pinStatus?.isSessionValid) {
            setThreadToLock(thread);
            setIsPinDialogOpen(true);
            return;
        }

        toggleLockMutation.mutate({ threadId: thread.id, lock: !isThreadLocked(thread) });
    };

    const {data: credits} = useQuery<UserCredits>({
        queryKey: ['/api/credits'],
        queryFn: async () => {
            const { creditsService } = await import("@/services/creditsService");
            return creditsService.getOrCreateUserCredits();
        },
    });

    const { data: creditsOverview } = useQuery<CreditsOverview>({
        queryKey: ["credits-overview"],
        queryFn: () => creditsService.getCreditsOverview(),
        staleTime: 0,
        refetchOnWindowFocus: true,
        refetchOnMount: "always",
    });

    /*const { data: threads = [] } = useQuery<ChatThread[]>({
      queryKey: ['/api/chat-threads'],
      staleTime: 0, // Always consider data stale to ensure fresh updates
      refetchOnWindowFocus: true,
    });*/

    const { data: threads = [] } = useQuery<ThreadSummary[]>({
        queryKey: ['/api/chat-thread-summaries', user?.id, currentWorkspaceId ?? 'personal'],
        queryFn: async () => {
            if (!user) return [];
            return await chatThreadsService.getThreadSummaries(currentWorkspaceId ?? null);
        },
        enabled: !!user,
        staleTime: 30000,
        refetchOnMount: true,
    });

    const sidebarThreadIds = useMemo(() => threads.map((thread) => String(thread.id)), [threads]);
    const { data: threadEmbeddingStatus = {} } = useQuery<Record<string, boolean>>({
        queryKey: ["thread-embedding-status", sidebarThreadIds.slice().sort().join(",")],
        queryFn: () => ragService.getThreadEmbeddingStatusForThreads(sidebarThreadIds),
        enabled: !!user && sidebarThreadIds.length > 0,
        staleTime: 60_000,
    });

    const handleSidebarThreadSearch = useCallback(async (
        thread: { id: string | number; title?: string | null },
        action: "embed" | "remove"
    ) => {
        const threadId = String(thread.id);
        const title = thread.title || "Thread";
        try {
            if (action === "remove") {
                await ragService.deleteThreadEmbeddings(threadId);
                toast({ title: "Removed from search", description: `"${title}" is no longer embedded.` });
            } else {
                await ragService.embedThread(threadId);
                toast({
                    title: threadEmbeddingStatus[threadId] ? "Re-embedded for search" : "Embedded for search",
                    description: threadEmbeddingStatus[threadId]
                        ? `"${title}" is updated for semantic search.`
                        : `"${title}" is now searchable by meaning.`,
                });
            }
            queryClient.invalidateQueries({ queryKey: ["thread-embedding-status"] });
        } catch (err) {
            toast({
                title: action === "remove" ? "Remove failed" : "Embed failed",
                description: err instanceof Error ? err.message : "Could not update thread embeddings.",
                variant: "destructive",
            });
        }
    }, [threadEmbeddingStatus, toast]);

    // const { threads } = useThreadSummaries({ enabled: !!user });

    const {data: spaces = []} = useQuery<MastermindSpace[]>({
        queryKey: ["/api/spaces", user?.id, currentWorkspaceId ?? "personal"],
        queryFn: async () => {
            if (!user) return [];
            return await mastermindSpacesService.getMastermindSpaces(currentWorkspaceId ?? null);
        },
        enabled: !!user,
        staleTime: 30000,
        refetchOnMount: true,
    });

    const handleThreadRecencyClick = useCallback((threadId: string) => {
        bumpThreadInSummariesCache(threadId);
    }, []);

    const handleSpaceRecencyClick = useCallback((spaceId: string) => {
        bumpSpaceInCache(spaceId);
    }, []);

    const {data: sources = []} = useQuery({
        queryKey: ['/api/sources', user?.id, currentWorkspaceId ?? 'personal'],
        queryFn: async () => {
            if (!user) return [];
            return await sourcesService.getSources(undefined, currentWorkspaceId ?? null);
        },
        enabled: !!user,
    });

    const { items: knowledgeItems } = useKnowledgeBaseList({ enabled: !!user });

    // Pinned Tools (client-side localStorage when API not deployed)
    const {data: pinnedToolsData} = useQuery<{ pinnedTools: string[]; maxPinned: number }>({
        queryKey: ['/api/pinned-tools'],
        queryFn: async () => {
            const { pinnedToolsService } = await import("@/services/pinnedToolsService");
            return pinnedToolsService.getPinnedTools();
        },
    });

    const pinnedTools = pinnedToolsData?.pinnedTools || [];
    const maxPinned = pinnedToolsData?.maxPinned || 6;

    const pinnedToolItems = useMemo(() => {
        return pinnedTools
            .map(url => toolsByUrl[url])
            .filter(Boolean);
    }, [pinnedTools]);

    const pinToolMutation = useMutation({
        mutationFn: async (toolUrl: string) => {
            const { pinnedToolsService } = await import("@/services/pinnedToolsService");
            return pinnedToolsService.addPinnedTool(toolUrl);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["/api/pinned-tools"]});
            toast({title: "Tool pinned", description: "Added to your quick access bar"});
        },
        onError: (error: Error) => {
            queryClient.invalidateQueries({queryKey: ["/api/pinned-tools"]});
            toast({title: "Error", description: error.message, variant: "destructive"});
        },
    });

    const unpinToolMutation = useMutation({
        mutationFn: async (toolUrl: string) => {
            const { pinnedToolsService } = await import("@/services/pinnedToolsService");
            return pinnedToolsService.removePinnedTool(toolUrl);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["/api/pinned-tools"]});
            toast({title: "Tool unpinned", description: "Removed from quick access bar"});
        },
        onError: (error: Error) => {
            queryClient.invalidateQueries({queryKey: ["/api/pinned-tools"]});
            toast({title: "Error", description: error.message, variant: "destructive"});
        },
    });

    const isPinned = (url: string) => pinnedTools.includes(url);
    const canPin = pinnedTools.length < maxPinned;

    const {data: topicsResponse} = useQuery<{ topics: Topic[]; total: number }>({
        queryKey: ['/api/topics'],
        queryFn: async () => {
            const { topicsService } = await import("@/services/topicsService");
            const list = await topicsService.getAllTopics();
            return { topics: list, total: list.length };
        },
    });
    const topics = topicsResponse?.topics || [];

    // Create lookup maps for quick access
    const spaceMap = new Map(spaces.map(s => [s.id, s]));
    const topicMap = new Map(topics.map(t => [t.id, t]));

    // Helper to get thread context info - accepts partial thread type
    const getThreadContext = (thread: { topicId?: string | null; linkedSpaceIds?: string[] | null }) => {
        const context: { spaceName?: string; topicName?: string; topicIcon?: string; spaceId?: string } = {};

        if (thread.topicId && topicMap.has(thread.topicId)) {
            const topic = topicMap.get(thread.topicId)!;
            context.topicName = topic.name;
            context.topicIcon = 'folder'; // Topics use color, not icon
            if (topic.spaceId && spaceMap.has(topic.spaceId)) {
                context.spaceName = spaceMap.get(topic.spaceId)!.name;
                context.spaceId = topic.spaceId;
            }
        } else if (thread.linkedSpaceIds && thread.linkedSpaceIds.length > 0) {
            const firstSpaceId = thread.linkedSpaceIds[0];
            if (spaceMap.has(firstSpaceId)) {
                context.spaceName = spaceMap.get(firstSpaceId)!.name;
                context.spaceId = firstSpaceId;
            }
        }

        return context;
    };

    const isThreadLocked = (thread: ThreadSummary) =>
        thread?.isLocked === "true" || thread?.isLocked === true;

    const isThreadArchived = (thread: ThreadSummary) =>
        thread.status === "archived" || String(thread.isArchived).toLowerCase() === "true";
    const activeThreads = threads.filter((thread) => !isThreadArchived(thread));
    const archivedThreads = threads.filter((thread) => isThreadArchived(thread));

    const getThreadTimestamp = (value?: string | number | Date | null, fallback = 0) => {
        const timestamp = new Date(value ?? fallback).getTime();
        return Number.isNaN(timestamp) ? fallback : timestamp;
    };

    const sortedActiveThreads = useMemo(() => {
        return [...activeThreads].sort((a, b) => {
            const dateA = getThreadTimestamp(a.updatedAt ?? a.createdAt);
            const dateB = getThreadTimestamp(b.updatedAt ?? b.createdAt);
            return dateB - dateA;
        });
    }, [activeThreads]);

    const sortedArchivedThreads = useMemo(() => {
        return [...archivedThreads].sort((a, b) => {
            const dateA = getThreadTimestamp(a.archivedAt ?? a.updatedAt ?? a.createdAt);
            const dateB = getThreadTimestamp(b.archivedAt ?? b.updatedAt ?? b.createdAt);
            return dateB - dateA;
        });
    }, [archivedThreads]);

    const sortedSpaces = useMemo(() => {
        const seenIds = new Set<string>();
        const deduped = spaces.filter((s) => {
            if (seenIds.has(s.id)) return false;
            seenIds.add(s.id);
            return true;
        });
        return [...deduped].sort((a, b) => {
            // Get the most recent timestamp for each space (updatedAt takes priority)
            const dateStrA = a.updatedAt ?? a.createdAt;
            const dateStrB = b.updatedAt ?? b.createdAt;
            
            // Convert to timestamps for comparison
            const timestampA = new Date(dateStrA).getTime();
            const timestampB = new Date(dateStrB).getTime();
            
            // Sort descending (most recent first)
            // If timestamps are invalid (NaN), treat them as 0
            const timeA = isNaN(timestampA) ? 0 : timestampA;
            const timeB = isNaN(timestampB) ? 0 : timestampB;
            
            return timeB - timeA;
        });
    }, [spaces]);

    const creditBalance = parseInt(credits?.balance || "0");
    const subscription = credits?.subscription;
    const planName = subscription?.plan?.name || "free";

    // Use plan-configured quotas: Free=1000, Pro=10000, Enterprise=50000
    const getMonthlyCreditsForPlan = (plan: string): number => {
        switch (plan.toLowerCase()) {
            case "pro":
                return 10000;
            case "enterprise":
                return 50000;
            default:
                return 1000;
        }
    };
    const monthlyCredits = creditsOverview?.monthlyAllowance ?? (
        subscription?.plan?.monthlyCredits
            ? parseInt(subscription.plan.monthlyCredits)
            : getMonthlyCreditsForPlan(planName)
    );

    const remainingCredits = creditsOverview?.balance ?? creditBalance;
    const isUnlimitedAiCredits = creditsOverview?.unlimitedAiCredits === true;
    const creditPercentage = isUnlimitedAiCredits
        ? 100
        : monthlyCredits > 0
            ? Math.round((remainingCredits / monthlyCredits) * 100)
            : 0;

    const getPlanBadge = () => {
        switch (planName.toLowerCase()) {
            case "pro":
                return {label: "Pro", variant: "default" as const, icon: Zap};
            case "enterprise":
                return {label: "Enterprise", variant: "default" as const, icon: Crown};
            default:
                return {label: "Free", variant: "secondary" as const, icon: null};
        }
    };

    const _planBadge = getPlanBadge();
    void _planBadge; // Reserved for future use

    const _getOrphanThreads = () => {
        return threads
            .filter(t => !t.topicId)
            .sort((a, b) => {
                const dateA = new Date(a.updatedAt || a.createdAt || 0).getTime();
                const dateB = new Date(b.updatedAt || b.createdAt || 0).getTime();
                return dateB - dateA; // Most recent first
            });
    };
    void _getOrphanThreads; // Reserved for future use

    return (
        <>
            <Sidebar collapsible="icon">
                <SidebarHeader className="p-4 group-data-[collapsible=icon]:px-1 group-data-[collapsible=icon]:py-4">
                    <div className="flex items-center gap-2">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-10 w-10 shrink-0 p-0 bg-primary hover:bg-primary/90 group-data-[collapsible=icon]:relative group-data-[collapsible=icon]:left-[0px] group-data-[collapsible=icon]:bg-transparent group-data-[collapsible=icon]:hover:bg-accent group-data-[collapsible=icon]:h-10 group-data-[collapsible=icon]:w-10"
                                onClick={toggleSidebar}
                                data-testid="button-sidebar-toggle"
                            >
                                <Brain
                                    className="h-6 w-6 text-primary-foreground group-data-[collapsible=icon]:hidden"/>
                                <PanelLeft className="h-5 w-5 hidden group-data-[collapsible=icon]:block"/>
                            </Button>
                            <Link href="/"
                                  className="flex flex-col min-w-0 overflow-hidden group-data-[collapsible=icon]:hidden hover-elevate active-elevate-2 rounded-md px-2 py-1 -mx-2 -my-1"
                                  data-testid="link-logo-home">
                                <span className="text-lg font-semibold truncate">DEEPLY SMART</span>
                                <span
                                    className="text-xs text-muted-foreground truncate">AI-powered life management</span>
                            </Link>
                        </div>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 shrink-0 group-data-[collapsible=icon]:hidden"
                            onClick={toggleSidebar}
                            data-testid="button-sidebar-toggle-alt"
                        >
                            <PanelLeft className="h-4 w-4"/>
                        </Button>
                    </div>
                    <Dialog open={createWorkspaceOpen} onOpenChange={setCreateWorkspaceOpen}>
                        <DialogContent className="sm:max-w-sm" onPointerDownOutside={(e) => e.preventDefault()}>
                            <DialogHeader>
                                <DialogTitle>Create workspace</DialogTitle>
                                <DialogDescription>Create a shared workspace. You can invite others later.</DialogDescription>
                            </DialogHeader>
                            <div className="grid gap-4 py-4">
                                <div className="grid gap-2">
                                    <Label htmlFor="workspace-name">Name</Label>
                                    <Input
                                        id="workspace-name"
                                        placeholder="e.g. Acme Corp"
                                        value={newWorkspaceName}
                                        onChange={(e) => setNewWorkspaceName(e.target.value)}
                                    />
                                </div>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setCreateWorkspaceOpen(false)}>Cancel</Button>
                                <Button
                                    onClick={async () => {
                                        const name = newWorkspaceName.trim();
                                        if (!name) return;
                                        try {
                                            const w = await workspacesService.createWorkspace({ name });
                                            await refetchWorkspaces();
                                            setCurrentWorkspaceId(w.id);
                                            setCreateWorkspaceOpen(false);
                                            setNewWorkspaceName("");
                                            toast({ title: "Workspace created", description: `"${w.name}" is ready.` });
                                            setLocation("/");
                                        } catch (err) {
                                            toast({ title: "Error", description: (err as Error).message, variant: "destructive" });
                                        }
                                    }}
                                    disabled={!newWorkspaceName.trim()}
                                >
                                    Create
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </SidebarHeader>
                <SidebarContent>
                    {/* Universal search: Boolean full-text search over all threads + documents (⌘K) */}
                    <SidebarUniversalSearch />
                    {/* Workspace switcher — DS EDIT 5aug26: single row (label folded into the trigger);
                        "Manage workspaces" lives inside the dropdown. */}
                    <div className="flex items-center px-3 py-2 group-data-[collapsible=icon]:px-1 group-data-[collapsible=icon]:py-0 group-data-[collapsible=icon]:justify-center">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    disabled={isLoadingWorkspaces}
                                    title="Switch workspace"
                                    aria-label="Switch workspace"
                                    className="h-auto w-full min-w-0 justify-start gap-2 rounded-md border border-transparent bg-muted/50 px-2 py-1.5 text-xs font-medium text-foreground hover:bg-muted/80 group-data-[collapsible=icon]:h-10 group-data-[collapsible=icon]:w-10 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:p-0 group-data-[collapsible=icon]:bg-muted/50"
                                >
                                    <Layers className="h-4 w-4 shrink-0 text-muted-foreground" />
                                    <span className="truncate text-left font-medium group-data-[collapsible=icon]:sr-only">
                                        {isLoadingWorkspaces
                                            ? "Loading…"
                                            : isWorkspaceContext
                                                ? (workspaces.find((w) => w.id === currentWorkspaceId)?.name ?? "Workspace")
                                                : "Personal"}
                                    </span>
                                    <ChevronDown className="ml-auto h-3 w-3 shrink-0 text-muted-foreground group-data-[collapsible=icon]:hidden" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="min-w-56 max-w-[min(100vw-2rem,280px)]">
                                {currentWorkspaceId !== null && (
                                    <DropdownMenuItem
                                        onClick={() => {
                                            setCurrentWorkspaceId(null);
                                            setLocation("/");
                                        }}
                                    >
                                        Personal
                                    </DropdownMenuItem>
                                )}
                                {!isLoadingWorkspaces &&
                                    workspaces
                                        .filter((w) => w.id !== currentWorkspaceId)
                                        .map((w) => (
                                            <DropdownMenuItem
                                                key={w.id}
                                                onClick={() => {
                                                    setCurrentWorkspaceId(w.id);
                                                    setLocation("/");
                                                }}
                                            >
                                                <span className="truncate">{w.name}</span>
                                            </DropdownMenuItem>
                                        ))}
                                {!isLoadingWorkspaces &&
                                    (currentWorkspaceId !== null ||
                                        workspaces.some((w) => w.id !== currentWorkspaceId)) && (
                                        <DropdownMenuSeparator />
                                    )}
                                <DropdownMenuItem
                                    onClick={() => {
                                        setCreateWorkspaceOpen(true);
                                        setNewWorkspaceName("");
                                    }}
                                >
                                    <Plus className="mr-2 h-4 w-4 shrink-0" />
                                    Create workspace
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setLocation("/workspaces")}>
                                    <Layers className="mr-2 h-4 w-4 shrink-0" />
                                    Manage workspaces
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                    <SidebarGroup>
                        <div
                            className="px-3 py-2 group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:py-0 group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:justify-center">
                            <Link href="/chat" className="w-full" onClick={() => setCurrentThreadId(null)}>
                                <Button
                                    className="w-full rounded-lg group-data-[collapsible=icon]:w-10 group-data-[collapsible=icon]:h-10 group-data-[collapsible=icon]:aspect-square group-data-[collapsible=icon]:rounded-md group-data-[collapsible=icon]:bg-primary group-data-[collapsible=icon]:text-primary-foreground group-data-[collapsible=icon]:hover:bg-primary/90"
                                    size="default" data-testid="button-capture-knowledge">
                                    <Plus className="h-4 w-4 group-data-[collapsible=icon]:mr-0 mr-2"/>
                                    <span className="group-data-[collapsible=icon]:hidden">New AI Smart Chat</span>
                                </Button>
                            </Link>
                        </div>
                    </SidebarGroup>

                    <SidebarGroup className="py-0">
                        <SidebarGroupContent>
                            <SidebarMenu>
                                <SidebarMenuItem>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <SidebarMenuButton
                                                asChild
                                                isActive={location === "/" || location === "/home"}
                                                data-testid="link-home"
                                            >
                                                <Link href="/">
                                                    <Home className="h-4 w-4"/>
                                                    <span
                                                        className="flex-1 truncate group-data-[collapsible=icon]:sr-only">Home</span>
                                                </Link>
                                            </SidebarMenuButton>
                                        </TooltipTrigger>
                                        <TooltipContent side="right">Home</TooltipContent>
                                    </Tooltip>
                                </SidebarMenuItem>

                    

                                <Collapsible open={recentChatsOpen} onOpenChange={setRecentChatsOpen}>
                                    <SidebarMenuItem>
                                        {isSidebarCollapsed ? (
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <SidebarMenuButton asChild isActive={location === "/threads"} data-testid="link-recent-collapsed">
                                                        <Link href="/threads">
                                                            <MessageSquare className="h-4 w-4"/>
                                                            <span className="flex-1 truncate group-data-[collapsible=icon]:sr-only">Recent</span>
                                                        </Link>
                                                    </SidebarMenuButton>
                                                </TooltipTrigger>
                                                <TooltipContent side="right">Threads</TooltipContent>
                                            </Tooltip>
                                        ) : (
                                            <SidebarMenuButton
                                                className="w-full"
                                                onClick={() => setRecentChatsOpen(!recentChatsOpen)}
                                                data-testid="button-toggle-recent-chats"
                                            >
                                                <MessageSquare className="h-4 w-4"/>
                                                <span
                                                    className="flex-1 truncate group-data-[collapsible=icon]:sr-only">Recent</span>
                                                {activeThreads.length > 0 && (
                                                    <Badge variant="secondary"
                                                           className="text-xs h-5 px-1.5 group-data-[collapsible=icon]:hidden">
                                                        {activeThreads.length}
                                                    </Badge>
                                                )}
                                                <span className="group-data-[collapsible=icon]:hidden">
                          {recentChatsOpen ? (
                              <ChevronDown className="h-3 w-3"/>
                          ) : (
                              <ChevronRight className="h-3 w-3"/>
                          )}
                        </span>
                                            </SidebarMenuButton>
                                        )}
                                    </SidebarMenuItem>
                                    {!isSidebarCollapsed && (
                                    <CollapsibleContent>
                                        <SidebarMenu className="ml-4 border-l border-border pl-2">
                                            <SidebarMenuItem>
                                                <SidebarMenuButton asChild className="text-primary font-medium">
                                                    <Link href="/threads" data-testid="link-see-all-threads">
                                                        <Layers className="h-4 w-4"/>
                                                        <span className="group-data-[collapsible=icon]:sr-only">See All Chats</span>
                                                    </Link>
                                                </SidebarMenuButton>
                                            </SidebarMenuItem>
                                            {sortedActiveThreads
                                                .slice(0, 10)
                                                .map((thread) => {
                                                    const context = getThreadContext(thread);
                                                    const topicIconData = context.topicIcon ? getTopicIcon(context.topicIcon) : null;
                                                    const TopicIcon = topicIconData?.icon;
                                                    return (
                                                        <SidebarMenuItem key={thread.id} className="group/thread">
                                                            <div className="flex items-center w-full gap-1">
                                                                <Tooltip>
                                                                    <TooltipTrigger className="flex-1 min-w-0 overflow-hidden">
                                                                        <SidebarMenuButton
                                                                            asChild
                                                                            isActive={activeThreadId === thread.id}
                                                                            data-testid={`thread-${thread.id}`}
                                                                        >
                                                                            <Link
                                                                                href={`/chat?threadId=${thread.id}`}
                                                                                onClick={(e) => {
                                                                                    if (isThreadLocked(thread) && !pinStatus?.isSessionValid) {
                                                                                        e.preventDefault();
                                                                                        setPendingThreadIdToOpen(thread.id);
                                                                                        return;
                                                                                    }
                                                                                    handleThreadRecencyClick(thread.id);
                                                                                }}
                                                                            >
                                                                                <MessageSquare
                                                                                    className="h-4 w-4 shrink-0"/>
                                                                                {isThreadLocked(thread) && (
                                                                                    <Lock className="h-3.5 w-3 shrink-0 text-red-500" />
                                                                                )}
                                                                                <div
                                                                                    className="flex flex-col min-w-0 flex-1 group-data-[collapsible=icon]:sr-only">
                                                                                    <span className="truncate text-sm inline-flex items-center gap-1 min-w-0">
                                                                                        <span className="truncate">{thread.title || "Untitled Thread"}</span>
                                                                                        {threadEmbeddingStatus[String(thread.id)] && <EmbeddedSearchMark className="h-3 w-3" />}
                                                                                    </span>
                                                                                    {(context.spaceName || context.topicName) && (
                                                                                        <div
                                                                                            className="flex items-center gap-1 text-[10px] text-muted-foreground">
                                                                                            {context.spaceName && (
                                                                                                <span
                                                                                                    className="flex items-center gap-0.5 truncate">
                                                <Layers className="h-2.5 w-2.5 shrink-0"/>
                                                <span className="truncate max-w-[60px]">{context.spaceName}</span>
                                              </span>
                                                                                            )}
                                                                                            {context.spaceName && context.topicName && (
                                                                                                <span
                                                                                                    className="text-muted-foreground/50">/</span>
                                                                                            )}
                                                                                            {context.topicName && TopicIcon && (
                                                                                                <span
                                                                                                    className="flex items-center gap-0.5 truncate">
                                                <TopicIcon className="h-2.5 w-2.5 shrink-0"/>
                                                <span className="truncate max-w-[60px]">{context.topicName}</span>
                                              </span>
                                                                                            )}
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            </Link>
                                                                        </SidebarMenuButton>
                                                                    </TooltipTrigger>
                                                                    <TooltipContent side="right">
                                                                        <div className="flex flex-col">
                                                                            <span>{thread.title || "Untitled Thread"}</span>
                                                                            {(context.spaceName || context.topicName) && (
                                                                                <span
                                                                                    className="text-xs text-muted-foreground">
                                        {context.spaceName}{context.spaceName && context.topicName && " / "}{context.topicName}
                                      </span>
                                                                            )}
                                                                        </div>
                                                                    </TooltipContent>
                                                                </Tooltip>
                                                                <DropdownMenu>
                                                                    <DropdownMenuTrigger asChild>
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="icon"
                                                                            className="h-6 w-6 shrink-0 opacity-0 group-hover/thread:opacity-100 transition-opacity"
                                                                            data-testid={`button-thread-menu-${thread.id}`}
                                                                        >
                                                                            <MoreHorizontal className="h-3 w-3"/>
                                                                        </Button>
                                                                    </DropdownMenuTrigger>
                                                                    <DropdownMenuContent align="end" className="w-48">
                                                                        <DropdownMenuItem
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setRenameValue(thread.title || "");
                                                                                setThreadToRename(thread);
                                                                            }}
                                                                            data-testid={`button-rename-thread-${thread.id}`}
                                                                        >
                                                                            <Pencil className="h-4 w-4 mr-2"/> Rename
                                                                        </DropdownMenuItem>
                                                                        <DropdownMenuItem
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setThreadToLink(thread);
                                                                            }}
                                                                            data-testid={`button-link-thread-${thread.id}`}
                                                                        >
                                                                            <Link2 className="h-4 w-4 mr-2"/> Link to Space/Topic
                                                                        </DropdownMenuItem>
                                                                        <DropdownMenuItem
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                _handleLockClick(thread);
                                                                            }}
                                                                            data-testid={`button-lock-thread-${thread.id}`}
                                                                        >
                                                                            {isThreadLocked(thread) ? (
                                                                                <><PinOff className="h-4 w-4 mr-2"/> Unlock Thread</>
                                                                            ) : (
                                                                                <><Lock className="h-4 w-4 mr-2"/> Lock Thread</>
                                                                            )}
                                                                        </DropdownMenuItem>
                                                                        <DropdownMenuSeparator/>
                                                                        {threadEmbeddingStatus[String(thread.id)] ? (
                                                                            <>
                                                                                <DropdownMenuItem
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        void handleSidebarThreadSearch(thread, "embed");
                                                                                    }}
                                                                                >
                                                                                    <Sparkles className="h-4 w-4 mr-2"/> Re-embed for search
                                                                                </DropdownMenuItem>
                                                                                <DropdownMenuItem
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        void handleSidebarThreadSearch(thread, "remove");
                                                                                    }}
                                                                                >
                                                                                    <X className="h-4 w-4 mr-2"/> Remove from search
                                                                                </DropdownMenuItem>
                                                                            </>
                                                                        ) : (
                                                                            <DropdownMenuItem
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    void handleSidebarThreadSearch(thread, "embed");
                                                                                }}
                                                                            >
                                                                                <Sparkles className="h-4 w-4 mr-2"/> Embed for search
                                                                            </DropdownMenuItem>
                                                                        )}
                                                                        <DropdownMenuSeparator/>
                                                                        <DropdownMenuItem
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                archiveThreadMutation.mutate({threadId: thread.id, archived: true});
                                                                            }}
                                                                            data-testid={`button-archive-thread-${thread.id}`}
                                                                        >
                                                                            <Archive className="h-4 w-4 mr-2"/> Archive
                                                                        </DropdownMenuItem>
                                                                        <DropdownMenuSeparator/>
                                                                        <DropdownMenuItem
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setThreadToDelete(thread);
                                                                            }}
                                                                            className="text-destructive focus:text-destructive"
                                                                            data-testid={`button-delete-thread-${thread.id}`}
                                                                        >
                                                                            <Trash2 className="h-4 w-4 mr-2"/>
                                                                            Delete
                                                                        </DropdownMenuItem>
                                                                    </DropdownMenuContent>
                                                                </DropdownMenu>
                                                            </div>
                                                        </SidebarMenuItem>
                                                    );
                                                })}
                                            <Collapsible open={archivedChatsOpen} onOpenChange={setArchivedChatsOpen}>
                                                <SidebarMenuItem>
                                                    <SidebarMenuButton
                                                        className="w-full"
                                                        onClick={() => setArchivedChatsOpen(!archivedChatsOpen)}
                                                        data-testid="button-toggle-archived-chats"
                                                    >
                                                        <Archive className="h-4 w-4"/>
                                                        <span
                                                            className="flex-1 truncate group-data-[collapsible=icon]:sr-only">Archived</span>
                                                        {archivedThreads.length > 0 && (
                                                            <Badge variant="secondary"
                                                                   className="text-xs h-5 px-1.5 group-data-[collapsible=icon]:hidden">
                                                                {archivedThreads.length}
                                                            </Badge>
                                                        )}
                                                        <span className="group-data-[collapsible=icon]:hidden">
                                                            {archivedChatsOpen ? (
                                                                <ChevronDown className="h-3 w-3"/>
                                                            ) : (
                                                                <ChevronRight className="h-3 w-3"/>
                                                            )}
                                                        </span>
                                                    </SidebarMenuButton>
                                                </SidebarMenuItem>
                                                <CollapsibleContent>
                                                    <SidebarMenu className="ml-4 border-l border-border pl-2">
                                                        {archivedThreads.length === 0 ? (
                                                            <SidebarMenuItem>
                                                                <SidebarMenuButton className="text-muted-foreground" data-testid="text-no-archived-chats">
                                                                    No archived chats
                                                                </SidebarMenuButton>
                                                            </SidebarMenuItem>
                                                        ) : (
                                                            sortedArchivedThreads
                                                                .slice(0, 10)
                                                                .map((thread) => {
                                                                    const context = getThreadContext(thread);
                                                                    const topicIconData = context.topicIcon ? getTopicIcon(context.topicIcon) : null;
                                                                    const TopicIcon = topicIconData?.icon;
                                                                    return (
                                                                        <SidebarMenuItem key={thread.id} className="group/thread">
                                                                            <div className="flex items-center w-full gap-1">
                                                                                <Tooltip>
                                                                                    <TooltipTrigger className="flex-1 min-w-0 overflow-hidden">
                                                                                        <SidebarMenuButton
                                                                                            asChild
                                                                                            isActive={activeThreadId === thread.id}
                                                                                            data-testid={`archived-thread-${thread.id}`}
                                                                                        >
                                                                                            <Link
                                                                                                href={`/chat?threadId=${thread.id}`}
                                                                                                onClick={(e) => {
                                                                                                    if (isThreadLocked(thread) && !pinStatus?.isSessionValid) {
                                                                                                        e.preventDefault();
                                                                                                        setPendingThreadIdToOpen(thread.id);
                                                                                                        return;
                                                                                                    }
                                                                                                    handleThreadRecencyClick(thread.id);
                                                                                                }}
                                                                                            >
                                                                                                <Archive
                                                                                                    className="h-4 w-4 shrink-0 text-muted-foreground"/>
                                                                                                {isThreadLocked(thread) && (
                                                                                                    <Lock className="h-3.5 w-3 shrink-0 text-red-500" />
                                                                                                )}
                                                                                                <div
                                                                                                    className="flex flex-col min-w-0 flex-1 group-data-[collapsible=icon]:sr-only">
                                                                                                    <span className="truncate text-sm inline-flex items-center gap-1 min-w-0">
                                                                                                        <span className="truncate">{thread.title || "Untitled Thread"}</span>
                                                                                                        {threadEmbeddingStatus[String(thread.id)] && <EmbeddedSearchMark className="h-3 w-3" />}
                                                                                                    </span>
                                                                                                    {(context.spaceName || context.topicName) && (
                                                                                                        <div
                                                                                                            className="flex items-center gap-1 text-[10px] text-muted-foreground">
                                                                                                            {context.spaceName && (
                                                                                                                <span
                                                                                                                    className="flex items-center gap-0.5 truncate">
                                                                                                                    <Layers className="h-2.5 w-2.5 shrink-0"/>
                                                                                                                    <span className="truncate max-w-[60px]">{context.spaceName}</span>
                                                                                                                </span>
                                                                                                            )}
                                                                                                            {context.spaceName && context.topicName && (
                                                                                                                <span
                                                                                                                    className="text-muted-foreground/50">/</span>
                                                                                                            )}
                                                                                                            {context.topicName && TopicIcon && (
                                                                                                                <span
                                                                                                                    className="flex items-center gap-0.5 truncate">
                                                                                                                    <TopicIcon className="h-2.5 w-2.5 shrink-0"/>
                                                                                                                    <span className="truncate max-w-[60px]">{context.topicName}</span>
                                                                                                                </span>
                                                                                                            )}
                                                                                                        </div>
                                                                                                    )}
                                                                                                </div>
                                                                                            </Link>
                                                                                        </SidebarMenuButton>
                                                                                    </TooltipTrigger>
                                                                                    <TooltipContent side="right">
                                                                                        <div className="flex flex-col">
                                                                                            <span>{thread.title || "Untitled Thread"}</span>
                                                                                            {(context.spaceName || context.topicName) && (
                                                                                                <span
                                                                                                    className="text-xs text-muted-foreground">
                                                                                                    {context.spaceName}{context.spaceName && context.topicName && " / "}{context.topicName}
                                                                                                </span>
                                                                                            )}
                                                                                        </div>
                                                                                    </TooltipContent>
                                                                                </Tooltip>
                                                                                <DropdownMenu>
                                                                                    <DropdownMenuTrigger asChild>
                                                                                        <Button
                                                                                            variant="ghost"
                                                                                            size="icon"
                                                                                            className="h-6 w-6 shrink-0 opacity-0 group-hover/thread:opacity-100 transition-opacity"
                                                                                            data-testid={`button-archived-thread-menu-${thread.id}`}
                                                                                        >
                                                                                            <MoreHorizontal className="h-3 w-3"/>
                                                                                        </Button>
                                                                                    </DropdownMenuTrigger>
                                                                                    <DropdownMenuContent align="end" className="w-48">
                                                                                        <DropdownMenuItem
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                setRenameValue(thread.title || "");
                                                                                                setThreadToRename(thread);
                                                                                            }}
                                                                                            data-testid={`button-rename-archived-thread-${thread.id}`}
                                                                                        >
                                                                                            <Pencil className="h-4 w-4 mr-2"/> Rename
                                                                                        </DropdownMenuItem>
                                                                                        <DropdownMenuItem
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                setThreadToLink(thread);
                                                                                            }}
                                                                                            data-testid={`button-link-archived-thread-${thread.id}`}
                                                                                        >
                                                                                            <Link2 className="h-4 w-4 mr-2"/> Link to Space/Topic
                                                                                        </DropdownMenuItem>
                                                                                        <DropdownMenuItem
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                _handleLockClick(thread);
                                                                                            }}
                                                                                            data-testid={`button-lock-archived-thread-${thread.id}`}
                                                                                        >
                                                                                            {isThreadLocked(thread) ? (
                                                                                                <><PinOff className="h-4 w-4 mr-2"/> Unlock Thread</>
                                                                                            ) : (
                                                                                                <><Lock className="h-4 w-4 mr-2"/> Lock Thread</>
                                                                                            )}
                                                                                        </DropdownMenuItem>
                                                                                        <DropdownMenuSeparator/>
                                                                                        {threadEmbeddingStatus[String(thread.id)] ? (
                                                                                            <>
                                                                                                <DropdownMenuItem
                                                                                                    onClick={(e) => {
                                                                                                        e.stopPropagation();
                                                                                                        void handleSidebarThreadSearch(thread, "embed");
                                                                                                    }}
                                                                                                >
                                                                                                    <Sparkles className="h-4 w-4 mr-2"/> Re-embed for search
                                                                                                </DropdownMenuItem>
                                                                                                <DropdownMenuItem
                                                                                                    onClick={(e) => {
                                                                                                        e.stopPropagation();
                                                                                                        void handleSidebarThreadSearch(thread, "remove");
                                                                                                    }}
                                                                                                >
                                                                                                    <X className="h-4 w-4 mr-2"/> Remove from search
                                                                                                </DropdownMenuItem>
                                                                                            </>
                                                                                        ) : (
                                                                                            <DropdownMenuItem
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    void handleSidebarThreadSearch(thread, "embed");
                                                                                                }}
                                                                                            >
                                                                                                <Sparkles className="h-4 w-4 mr-2"/> Embed for search
                                                                                            </DropdownMenuItem>
                                                                                        )}
                                                                                        <DropdownMenuSeparator/>
                                                                                        <DropdownMenuItem
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                archiveThreadMutation.mutate({threadId: thread.id, archived: false});
                                                                                            }}
                                                                                            data-testid={`button-unarchive-thread-${thread.id}`}
                                                                                        >
                                                                                            <ArchiveRestore className="h-4 w-4 mr-2"/> Unarchive
                                                                                        </DropdownMenuItem>
                                                                                        <DropdownMenuSeparator/>
                                                                                        <DropdownMenuItem
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                setThreadToDelete(thread);
                                                                                            }}
                                                                                            className="text-destructive focus:text-destructive"
                                                                                            data-testid={`button-delete-archived-thread-${thread.id}`}
                                                                                        >
                                                                                            <Trash2 className="h-4 w-4 mr-2"/>
                                                                                            Delete
                                                                                        </DropdownMenuItem>
                                                                                    </DropdownMenuContent>
                                                                                </DropdownMenu>
                                                                            </div>
                                                                        </SidebarMenuItem>
                                                                    );
                                                                })
                                                        )}
                                                    </SidebarMenu>
                                                </CollapsibleContent>
                                            </Collapsible>
                                        </SidebarMenu>
                                    </CollapsibleContent>
                                    )}
                                </Collapsible>

                                {/* Pinned Tools - Quick Access */}
                                {pinnedToolItems.length > 0 && (
                                    <>
                                        <SidebarGroupLabel
                                            className="text-xs text-muted-foreground px-2 py-1 group-data-[collapsible=icon]:hidden">
                                            Quick Access
                                        </SidebarGroupLabel>
                                        {pinnedToolItems.map((item) => (
                                            <SidebarMenuItem key={item.url} className="group/pin">
                                                <div className="flex items-center w-full">
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <SidebarMenuButton asChild isActive={location === item.url}
                                                                               className="flex-1">
                                                                <Link href={item.url}
                                                                   data-testid={`link-pinned-${item.title.toLowerCase().replace(/\s+/g, '-')}`}>
                                                                    <item.icon className="h-4 w-4 shrink-0"/>
                                                                    <span
                                                                        className="truncate group-data-[collapsible=icon]:hidden">{item.title}</span>
                                                                </Link>
                                                            </SidebarMenuButton>
                                                        </TooltipTrigger>
                                                        <TooltipContent side="right">{item.title}</TooltipContent>
                                                    </Tooltip>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            unpinToolMutation.mutate(item.url);
                                                        }}
                                                        className="opacity-0 group-hover/pin:opacity-100 p-1 hover:bg-muted rounded-sm transition-opacity group-data-[collapsible=icon]:hidden"
                                                        data-testid={`button-unpin-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
                                                    >
                                                        <PinOff className="h-3 w-3 text-muted-foreground"/>
                                                    </button>
                                                </div>
                                            </SidebarMenuItem>
                                        ))}
                                        <div className="h-px bg-border my-2 group-data-[collapsible=icon]:hidden"/>
                                    </>
                                )}

                                <Collapsible open={spacesOpen} onOpenChange={setSpacesOpen}>
                                    <SidebarMenuItem>
                                        {isSidebarCollapsed ? (
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <SidebarMenuButton asChild isActive={location === "/spaces"} data-testid="link-spaces-collapsed">
                                                        <Link href="/spaces">
                                                            <Layers className="h-4 w-4"/>
                                                            <span className="flex-1 truncate group-data-[collapsible=icon]:sr-only">Spaces</span>
                                                        </Link>
                                                    </SidebarMenuButton>
                                                </TooltipTrigger>
                                                <TooltipContent side="right">Spaces</TooltipContent>
                                            </Tooltip>
                                        ) : (
                                            <SidebarMenuButton
                                                className="w-full"
                                                onClick={() => setSpacesOpen(!spacesOpen)}
                                                data-testid="button-toggle-spaces"
                                            >
                                                <Layers className="h-4 w-4"/>
                                                <span
                                                    className="flex-1 truncate group-data-[collapsible=icon]:sr-only">Spaces</span>
                                                {spaces.length > 0 && (
                                                    <Badge variant="secondary"
                                                           className="text-xs h-5 px-1.5 group-data-[collapsible=icon]:hidden">
                                                        {spaces.length}
                                                    </Badge>
                                                )}
                                                <span className="group-data-[collapsible=icon]:hidden">
                                                    {spacesOpen ? (
                                                        <ChevronDown className="h-3 w-3"/>
                                                    ) : (
                                                        <ChevronRight className="h-3 w-3"/>
                                                    )}
                                                </span>
                                            </SidebarMenuButton>
                                        )}
                                    </SidebarMenuItem>
                                    {!isSidebarCollapsed && (
                                    <CollapsibleContent>
                                        <SidebarMenu className="ml-4 border-l border-border pl-2">
                                            <SidebarMenuItem>
                                                <SidebarMenuButton asChild className="text-primary font-medium">
                                                    <Link href="/spaces" data-testid="link-see-all-spaces">
                                                        <FolderOpen className="h-4 w-4"/>
                                                        <span className="group-data-[collapsible=icon]:sr-only">View All Spaces</span>
                                                    </Link>
                                                </SidebarMenuButton>
                                            </SidebarMenuItem>
                                            {sortedSpaces
                                                .slice(0, 10)
                                                .map((space) => (
                                                    <SidebarMenuItem key={space.id} className="group/space">
                                                        <div className="flex items-center w-full gap-1">
                                                            <Tooltip>
                                                                <TooltipTrigger className="flex-1 min-w-0 overflow-hidden">
                                                                    <SidebarMenuButton
                                                                        asChild
                                                                        isActive={location === `/spaces/${space.id}`}
                                                                        data-testid={`space-${space.id}`}
                                                                    >
                                                                        <Link
                                                                            href={`/spaces/${space.id}`}
                                                                            onClick={() => handleSpaceRecencyClick(space.id)}
                                                                        >
                                                                            <div
                                                                                className="w-3 h-3 rounded-sm shrink-0"
                                                                                style={{ backgroundColor: space.color || '#6366f1' }}
                                                                            />
                                                                            <span className="truncate text-sm group-data-[collapsible=icon]:sr-only">
                                                                                {space.name}
                                                                            </span>
                                                                        </Link>
                                                                    </SidebarMenuButton>
                                                                </TooltipTrigger>
                                                                <TooltipContent side="right">
                                                                    <span>{space.name}</span>
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        </div>
                                                    </SidebarMenuItem>
                                                ))}
                                        </SidebarMenu>
                                    </CollapsibleContent>
                                    )}
                                </Collapsible>

                                <SidebarMenuItem>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <SidebarMenuButton
                                                asChild={isFeatureUnlocked('library')}
                                                isActive={location.startsWith("/library")}
                                                className={!isFeatureUnlocked('library') ? 'opacity-50 cursor-not-allowed' : ''}
                                            >
                                                {isFeatureUnlocked('library') ? (
                                                    <Link href="/library" data-testid="link-library">
                                                        <BookOpen className="h-4 w-4"/>
                                                        <span className="flex-1 truncate group-data-[collapsible=icon]:sr-only">Library</span>
                                                        {(sources.length + knowledgeItems.length) > 0 && (
                                                            <Badge variant="secondary"
                                                                   className="text-xs h-5 px-1.5 group-data-[collapsible=icon]:hidden">
                                                                {sources.length + knowledgeItems.length}
                                                            </Badge>
                                                        )}
                                                    </Link>
                                                ) : (
                                                    <div className="flex items-center gap-2" data-testid="link-library-locked">
                                                        <Lock className="h-4 w-4 text-muted-foreground"/>
                                                        <span className="flex-1 truncate group-data-[collapsible=icon]:sr-only">Library</span>
                                                        <Badge variant="outline" className="text-xs h-5 px-1.5 group-data-[collapsible=icon]:hidden">
                                                            Locked
                                                        </Badge>
                                                    </div>
                                                )}
                                            </SidebarMenuButton>
                                        </TooltipTrigger>
                                        <TooltipContent side="right">
                                            {isFeatureUnlocked('library') ? 'Library' : 'Create your first Mastermind Space to unlock'}
                                        </TooltipContent>
                                    </Tooltip>
                                </SidebarMenuItem>

                                <Collapsible open={communicationOpen} onOpenChange={setCommunicationOpen}>
                                    <SidebarMenuItem>
                                        {isSidebarCollapsed ? (
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <SidebarMenuButton
                                                        asChild
                                                        isActive={location.startsWith("/fireflies") || location.startsWith("/communication/")}
                                                        data-testid="link-communication-collapsed"
                                                    >
                                                        <Link href="/communication/gohighlevel">
                                                            <MessageCircle className="h-4 w-4"/>
                                                            <span className="flex-1 truncate group-data-[collapsible=icon]:sr-only">Communication</span>
                                                        </Link>
                                                    </SidebarMenuButton>
                                                </TooltipTrigger>
                                                <TooltipContent side="right">Communication</TooltipContent>
                                            </Tooltip>
                                        ) : (
                                            <SidebarMenuButton
                                                className="w-full"
                                                onClick={() => setCommunicationOpen(!communicationOpen)}
                                                data-testid="button-toggle-communication"
                                            >
                                                <MessageCircle className="h-4 w-4"/>
                                                <span className="flex-1 truncate group-data-[collapsible=icon]:sr-only">Communication</span>
                                                <span className="group-data-[collapsible=icon]:hidden">
                                                    {communicationOpen ? (
                                                        <ChevronDown className="h-3 w-3"/>
                                                    ) : (
                                                        <ChevronRight className="h-3 w-3"/>
                                                    )}
                                                </span>
                                            </SidebarMenuButton>
                                        )}
                                    </SidebarMenuItem>
                                    {!isSidebarCollapsed && (
                                    <CollapsibleContent>
                                        <SidebarMenu className="ml-4 border-l border-border pl-2">
                                            {mergedCommunicationItems.map((item) => (
                                                <SidebarMenuItem key={item.url}>
                                                    <SidebarMenuButton
                                                        asChild
                                                        isActive={location === item.url}
                                                        data-testid={`link-${item.title.toLowerCase()}`}
                                                    >
                                                        <Link href={item.url}>
                                                            <item.icon className="h-4 w-4"/>
                                                            <span className="group-data-[collapsible=icon]:sr-only">{item.title}</span>
                                                        </Link>
                                                    </SidebarMenuButton>
                                                </SidebarMenuItem>
                                            ))}
                                        </SidebarMenu>
                                    </CollapsibleContent>
                                    )}
                                </Collapsible>

                                {/* Plugin-defined root sections */}
                                {pluginRootSections.map((section) => (
                                    <Collapsible
                                        key={section.id}
                                        open={pluginSectionsOpen[section.id] ?? false}
                                        onOpenChange={(open) =>
                                            setPluginSectionsOpen((prev) => ({...prev, [section.id]: open}))
                                        }
                                    >
                                        <SidebarMenuItem>
                                            <SidebarMenuButton
                                                className="w-full"
                                                onClick={() =>
                                                    setPluginSectionsOpen((prev) => ({
                                                        ...prev,
                                                        [section.id]: !(prev[section.id] ?? false),
                                                    }))
                                                }
                                                data-testid={`button-toggle-plugin-section-${section.id}`}
                                            >
                                                <Puzzle className="h-4 w-4"/>
                                                <span className="flex-1 truncate group-data-[collapsible=icon]:sr-only">{section.label}</span>
                                                <span className="group-data-[collapsible=icon]:hidden">
                                                    {(pluginSectionsOpen[section.id] ?? false) ? (
                                                        <ChevronDown className="h-3 w-3"/>
                                                    ) : (
                                                        <ChevronRight className="h-3 w-3"/>
                                                    )}
                                                </span>
                                            </SidebarMenuButton>
                                        </SidebarMenuItem>
                                        {!isSidebarCollapsed && (
                                        <CollapsibleContent>
                                            <SidebarMenu className="ml-4 border-l border-border pl-2">
                                                {pluginItemsToMenuItems(section.items).map((item) => (
                                                    <SidebarMenuItem key={item.url}>
                                                        <SidebarMenuButton
                                                            asChild
                                                            isActive={location === item.url}
                                                            data-testid={`link-plugin-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
                                                        >
                                                            <Link href={item.url}>
                                                                <item.icon className="h-4 w-4"/>
                                                                <span className="group-data-[collapsible=icon]:sr-only">{item.title}</span>
                                                            </Link>
                                                        </SidebarMenuButton>
                                                    </SidebarMenuItem>
                                                ))}
                                            </SidebarMenu>
                                        </CollapsibleContent>
                                        )}
                                    </Collapsible>
                                ))}

                                {/* Plugin manager */}
                                <SidebarMenuItem>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <SidebarMenuButton
                                                asChild
                                                isActive={location === "/plugins"}
                                                data-testid="link-plugins"
                                            >
                                                <Link href="/plugins">
                                                    <Puzzle className="h-4 w-4"/>
                                                    <span className="flex-1 truncate group-data-[collapsible=icon]:sr-only">Plugins</span>
                                                </Link>
                                            </SidebarMenuButton>
                                        </TooltipTrigger>
                                        <TooltipContent side="right">Plugins</TooltipContent>
                                    </Tooltip>
                                </SidebarMenuItem>

                                {/* Essential Tools - Always visible, most used */}
                                <Collapsible open={toolsOpen} onOpenChange={setToolsOpen}>
                                    <SidebarMenuItem>
                                        {isSidebarCollapsed && isFeatureUnlocked('tools') ? (
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <SidebarMenuButton asChild isActive={location === "/command-center"} data-testid="link-tools-collapsed">
                                                        <Link href="/command-center">
                                                            <Cpu className="h-4 w-4"/>
                                                            <span className="flex-1 truncate group-data-[collapsible=icon]:sr-only">Tools</span>
                                                        </Link>
                                                    </SidebarMenuButton>
                                                </TooltipTrigger>
                                                <TooltipContent side="right">Command Center</TooltipContent>
                                            </Tooltip>
                                        ) : (
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <SidebarMenuButton
                                                        className={`w-full ${!isFeatureUnlocked('tools') ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                        onClick={() => isFeatureUnlocked('tools') && setToolsOpen(!toolsOpen)}
                                                        data-testid="button-toggle-sidebar-tools"
                                                    >
                                                        {isFeatureUnlocked('tools') ? (
                                                            <Cpu className="h-4 w-4"/>
                                                        ) : (
                                                            <Lock className="h-4 w-4 text-muted-foreground"/>
                                                        )}
                                                        <span
                                                            className="flex-1 truncate group-data-[collapsible=icon]:sr-only">Tools</span>
                                                        {!isFeatureUnlocked('tools') && (
                                                            <Badge variant="outline" className="text-xs h-5 px-1.5 group-data-[collapsible=icon]:hidden">
                                                                Locked
                                                            </Badge>
                                                        )}
                                                        {isFeatureUnlocked('tools') && (
                                                            <span className="group-data-[collapsible=icon]:hidden">
                                                                {toolsOpen ? (
                                                                    <ChevronDown className="h-3 w-3"/>
                                                                ) : (
                                                                    <ChevronRight className="h-3 w-3"/>
                                                                )}
                                                            </span>
                                                        )}
                                                    </SidebarMenuButton>
                                                </TooltipTrigger>
                                                <TooltipContent side="right">
                                                    {isFeatureUnlocked('tools') ? 'AI Tools' : 'Visit Library to unlock AI Tools'}
                                                </TooltipContent>
                                            </Tooltip>
                                        )}
                                    </SidebarMenuItem>
                                    {!isSidebarCollapsed && (
                                    <CollapsibleContent>
                                        <SidebarMenu className="ml-4 border-l border-border pl-2">
                                            {mergedEssentialToolsItems.map((item) => (
                                                <SidebarMenuItem key={item.title} className="group/tool">
                                                    <div className="flex items-center w-full">
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <SidebarMenuButton asChild
                                                                                   isActive={location === item.url}
                                                                                   className="flex-1">
                                                                    <Link href={item.url}
                                                                       data-testid={`link-${item.title.toLowerCase().replace(/\s+/g, '-')}`}>
                                                                        <item.icon className="h-4 w-4 shrink-0"/>
                                                                        <span
                                                                            className="truncate group-data-[collapsible=icon]:hidden">{item.title}</span>
                                                                    </Link>
                                                                </SidebarMenuButton>
                                                            </TooltipTrigger>
                                                            <TooltipContent side="right">{item.title}</TooltipContent>
                                                        </Tooltip>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (isPinned(item.url)) {
                                                                    unpinToolMutation.mutate(item.url);
                                                                } else if (canPin) {
                                                                    pinToolMutation.mutate(item.url);
                                                                } else {
                                                                    toast({
                                                                        title: "Limit reached",
                                                                        description: `Maximum ${maxPinned} tools can be pinned`,
                                                                        variant: "destructive"
                                                                    });
                                                                }
                                                            }}
                                                            className="opacity-0 group-hover/tool:opacity-100 p-1 hover:bg-muted rounded-sm transition-opacity group-data-[collapsible=icon]:hidden"
                                                            data-testid={`button-pin-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
                                                        >
                                                            {isPinned(item.url) ? (
                                                                <PinOff className="h-3 w-3 text-muted-foreground"/>
                                                            ) : (
                                                                <Pin className="h-3 w-3 text-muted-foreground"/>
                                                            )}
                                                        </button>
                                                    </div>
                                                </SidebarMenuItem>
                                            ))}

                                            {/* Utility Items - Less frequently used */}
                                            <Collapsible open={utilityOpen} onOpenChange={setUtilityOpen}>
                                                <SidebarMenuItem>
                                                    <SidebarMenuButton
                                                        className="w-full"
                                                        onClick={() => setUtilityOpen(!utilityOpen)}
                                                        data-testid="button-toggle-utility"
                                                    >
                                                        <BarChart3 className="h-4 w-4"/>
                                                        <span
                                                            className="flex-1 truncate group-data-[collapsible=icon]:sr-only">More Tools</span>
                                                        <span className="group-data-[collapsible=icon]:hidden">
                            {utilityOpen ? (
                                <ChevronDown className="h-3 w-3"/>
                            ) : (
                                <ChevronRight className="h-3 w-3"/>
                            )}
                          </span>
                                                    </SidebarMenuButton>
                                                </SidebarMenuItem>
                                                <CollapsibleContent>
                                                    <SidebarMenu className="ml-4 border-l border-border pl-2">
                                                        {mergedUtilityItems.map((item) => (
                                                            <SidebarMenuItem key={item.title} className="group/tool">
                                                                <div className="flex items-center w-full">
                                                                    <Tooltip>
                                                                        <TooltipTrigger asChild>
                                                                            <SidebarMenuButton asChild
                                                                                               isActive={location === item.url}
                                                                                               className="flex-1">
                                                                                <Link href={item.url}
                                                                                   data-testid={`link-${item.title.toLowerCase().replace(/\s+/g, '-')}`}>
                                                                                    <item.icon
                                                                                        className="h-4 w-4 shrink-0"/>
                                                                                    <span
                                                                                        className="truncate group-data-[collapsible=icon]:hidden">{item.title}</span>
                                                                                </Link>
                                                                            </SidebarMenuButton>
                                                                        </TooltipTrigger>
                                                                        <TooltipContent
                                                                            side="right">{item.title}</TooltipContent>
                                                                    </Tooltip>
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            if (isPinned(item.url)) {
                                                                                unpinToolMutation.mutate(item.url);
                                                                            } else if (canPin) {
                                                                                pinToolMutation.mutate(item.url);
                                                                            } else {
                                                                                toast({
                                                                                    title: "Limit reached",
                                                                                    description: `Maximum ${maxPinned} tools can be pinned`,
                                                                                    variant: "destructive"
                                                                                });
                                                                            }
                                                                        }}
                                                                        className="opacity-0 group-hover/tool:opacity-100 p-1 hover:bg-muted rounded-sm transition-opacity group-data-[collapsible=icon]:hidden"
                                                                        data-testid={`button-pin-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
                                                                    >
                                                                        {isPinned(item.url) ? (
                                                                            <PinOff
                                                                                className="h-3 w-3 text-muted-foreground"/>
                                                                        ) : (
                                                                            <Pin
                                                                                className="h-3 w-3 text-muted-foreground"/>
                                                                        )}
                                                                    </button>
                                                                </div>
                                                            </SidebarMenuItem>
                                                        ))}
                                                    </SidebarMenu>
                                                </CollapsibleContent>
                                            </Collapsible>
                                        </SidebarMenu>
                                    </CollapsibleContent>
                                    )}
                                </Collapsible>

                                {/* Advanced Features - Only shown in Pro Mode */}
                                {proMode && (
                                    <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
                                        <SidebarMenuItem>
                                            <SidebarMenuButton
                                                className="w-full"
                                                onClick={() => setAdvancedOpen(!advancedOpen)}
                                                data-testid="button-toggle-advanced"
                                            >
                                                <Crown className="h-4 w-4"/>
                                                <span
                                                    className="flex-1 truncate group-data-[collapsible=icon]:sr-only">Advanced</span>
                                                <Badge variant="secondary"
                                                       className="text-[10px] h-4 px-1 group-data-[collapsible=icon]:hidden">Pro</Badge>
                                                <span className="group-data-[collapsible=icon]:hidden ml-1">
                        {advancedOpen ? (
                            <ChevronDown className="h-3 w-3"/>
                        ) : (
                            <ChevronRight className="h-3 w-3"/>
                        )}
                      </span>
                                            </SidebarMenuButton>
                                        </SidebarMenuItem>
                                        <CollapsibleContent>
                                            <SidebarMenu className="ml-4 border-l border-border pl-2">
                                                {mergedAdvancedItems.map((item) => (
                                                    <SidebarMenuItem key={item.title} className="group/tool">
                                                        <div className="flex items-center w-full">
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <SidebarMenuButton asChild
                                                                                       isActive={location === item.url}
                                                                                       className="flex-1">
                                                                        <Link href={item.url}
                                                                           data-testid={`link-${item.title.toLowerCase().replace(/\s+/g, '-')}`}>
                                                                            <item.icon className="h-4 w-4 shrink-0"/>
                                                                            <span
                                                                                className="truncate group-data-[collapsible=icon]:hidden">{item.title}</span>
                                                                        </Link>
                                                                    </SidebarMenuButton>
                                                                </TooltipTrigger>
                                                                <TooltipContent
                                                                    side="right">{item.title}</TooltipContent>
                                                            </Tooltip>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (isPinned(item.url)) {
                                                                        unpinToolMutation.mutate(item.url);
                                                                    } else if (canPin) {
                                                                        pinToolMutation.mutate(item.url);
                                                                    } else {
                                                                        toast({
                                                                            title: "Limit reached",
                                                                            description: `Maximum ${maxPinned} tools can be pinned`,
                                                                            variant: "destructive"
                                                                        });
                                                                    }
                                                                }}
                                                                className="opacity-0 group-hover/tool:opacity-100 p-1 hover:bg-muted rounded-sm transition-opacity group-data-[collapsible=icon]:hidden"
                                                                data-testid={`button-pin-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
                                                            >
                                                                {isPinned(item.url) ? (
                                                                    <PinOff className="h-3 w-3 text-muted-foreground"/>
                                                                ) : (
                                                                    <Pin className="h-3 w-3 text-muted-foreground"/>
                                                                )}
                                                            </button>
                                                        </div>
                                                    </SidebarMenuItem>
                                                ))}
                                            </SidebarMenu>
                                        </CollapsibleContent>
                                        </Collapsible>
                                )}
                            </SidebarMenu>
                        </SidebarGroupContent>
                    </SidebarGroup>

                    <SidebarGroup>
                        <SidebarGroupContent>
                            <SidebarMenu>
                                {bottomItems.map((item) => (
                                    <SidebarMenuItem key={item.title}>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <SidebarMenuButton asChild isActive={location === item.url}>
                                                    <Link href={item.url} data-testid={`link-${item.title.toLowerCase()}`}>
                                                        <item.icon className="h-4 w-4"/>
                                                        <span
                                                            className="group-data-[collapsible=icon]:sr-only">{item.title}</span>
                                                    </Link>
                                                </SidebarMenuButton>
                                            </TooltipTrigger>
                                            <TooltipContent side="right">{item.title}</TooltipContent>
                                        </Tooltip>
                                    </SidebarMenuItem>
                                ))}
                                  <SidebarMenuItem>
                                  <Tooltip>
                                        <TooltipTrigger asChild>
                                            <SidebarMenuButton
                                                asChild
                                                isActive={location === "/your-chats"}
                                                data-testid="link-your-chats"
                                            >
                                                <Link href="/your-chats">
                                                    <MessageSquare className="h-4 w-4"/>
                                                    <span
                                                        className="flex-1 truncate group-data-[collapsible=icon]:sr-only">Your chats</span>
                                                </Link>
                                            </SidebarMenuButton>
                                        </TooltipTrigger>
                                        <TooltipContent side="right">Your chats</TooltipContent>
                                    </Tooltip>
                                     </SidebarMenuItem>
                            </SidebarMenu>
                        </SidebarGroupContent>
                    </SidebarGroup>
                </SidebarContent>
                <SidebarFooter className="p-4 group-data-[collapsible=icon]:p-1.5">
                    <div className="flex items-center gap-2 min-w-0 group-data-[collapsible=icon]:flex-col group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:gap-1">
                        {/* AI Credits - Compact, clickable to subscription page */}
                        <Link href="/settings?tab=subscription" className="flex-1 min-w-0 group-data-[collapsible=icon]:flex-none">
                            <div className={`flex-1 rounded-lg p-2 group-data-[collapsible=icon]:p-2 cursor-pointer transition-colors hover:opacity-80 ${
                                remainingCredits === 0 && !isUnlimitedAiCredits
                                    ? 'bg-red-500/20 border border-red-500/50' 
                                    : 'bg-accent/50'
                            }`}>
                                {/* Warning message when credits depleted */}
                                {remainingCredits === 0 && !isUnlimitedAiCredits && (
                                    <p className="text-[10px] text-red-500 font-medium mb-1 group-data-[collapsible=icon]:hidden">
                                        No credits remaining
                                    </p>
                                )}
                                <div
                                    className="flex items-center gap-2 mb-1 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:mb-0">
                                    <Sparkles className={`h-3 w-3 ${remainingCredits === 0 && !isUnlimitedAiCredits ? 'text-red-500' : 'text-primary'}`}/>
                                </div>
                                <div className="space-y-1 group-data-[collapsible=icon]:hidden">
                                    <div className="h-1.5 bg-background rounded-full overflow-hidden">
                                        <div
                                            className={`h-full transition-all duration-300 ${remainingCredits === 0 && !isUnlimitedAiCredits ? 'bg-red-500' : 'bg-primary'}`}
                                            style={{width: `${Math.min(creditPercentage, 100)}%`}}
                                        ></div>
                                    </div>
                                    <p className={`text-[10px] ${remainingCredits === 0 && !isUnlimitedAiCredits ? 'text-red-500' : 'text-muted-foreground'}`} data-testid="text-credits-balance">
                                        {isUnlimitedAiCredits
                                            ? "Unlimited AI credits"
                                            : `Active: ${monthlyCredits.toLocaleString()} | Remaining: ${remainingCredits.toLocaleString()}`}
                                    </p>
                                </div>
                            </div>
                        </Link>

                        {/* User Icon, Install and Share Button - Stacked; column and centered when sidebar collapsed */}
                        <div className="flex flex-col gap-1 shrink-0 group-data-[collapsible=icon]:items-center">
                            <UserMenu/>
                            <InstallAppButton />
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-9 w-9 rounded-full shrink-0"
                                data-testid="button-share"
                            >
                                <Share2 className="h-4 w-4"/>
                            </Button>
                        </div>
                    </div>
                </SidebarFooter>
            </Sidebar>

            <AlertDialog open={!!threadToDelete} onOpenChange={(open) => !open && setThreadToDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Chat Thread</AlertDialogTitle>
                        <AlertDialogDescription>
                            Move "{threadToDelete?.title || "Untitled Thread"}" to trash? It will be kept for 30 days and can be restored from the Threads page.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="button-cancel-delete-thread">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => threadToDelete && deleteThreadMutation.mutate(threadToDelete.id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid="button-confirm-delete-thread"
                        >
                            {deleteThreadMutation.isPending ? "Deleting..." : "Delete"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* PIN Verification Dialog for thread locking */}
            <PinVerificationDialog
                open={isPinDialogOpen}
                onOpenChange={setIsPinDialogOpen}
                onSuccess={() => {
                    if (threadToLock) {
                        const shouldLock = !isThreadLocked(threadToLock as ThreadSummary);
                        toggleLockMutation.mutate({threadId: threadToLock.id, lock: shouldLock});
                    }
                }}
                title={threadToLock && isThreadLocked(threadToLock as ThreadSummary) ? "Unlock Thread" : "Lock Thread"}
                description="Enter your PIN to continue"
            />

            {/* PIN Verification Dialog for opening a locked thread */}
            <PinVerificationDialog
                open={!!pendingThreadIdToOpen}
                onOpenChange={(open) => !open && setPendingThreadIdToOpen(null)}
                onSuccess={() => {
                    if (pendingThreadIdToOpen) {
                        queryClient.invalidateQueries({ queryKey: ["/api/pin/status"] });
                        setLocation(`/chat?threadId=${pendingThreadIdToOpen}`);
                        setPendingThreadIdToOpen(null);
                    }
                }}
                title="Unlock Thread"
                description="Enter your PIN to open this locked thread"
            />

            {/* Rename Dialog */}
            <Dialog open={!!threadToRename} onOpenChange={(open) => !open && setThreadToRename(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Rename Thread</DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <Label htmlFor="rename-input">Thread Name</Label>
                        <Input
                            id="rename-input"
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            placeholder="Enter thread name..."
                            className="mt-2"
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && renameValue.trim() && threadToRename) {
                                    renameMutation.mutate({threadId: threadToRename.id, title: renameValue.trim()});
                                }
                            }}
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setThreadToRename(null)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={() => {
                                if (threadToRename && renameValue.trim()) {
                                    renameMutation.mutate({threadId: threadToRename.id, title: renameValue.trim()});
                                }
                            }}
                            disabled={!renameValue.trim() || renameMutation.isPending}
                        >
                            {renameMutation.isPending ? "Saving..." : "Save"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Linking Dialog */}
            {threadToLink && (
                <ThreadLinkingDialog
                    open={!!threadToLink}
                    onOpenChange={(open) => !open && setThreadToLink(null)}
                    linkedSpaceIds={threadToLink.linkedSpaceIds || []}
                    topicId={threadToLink.topicId || null}
                    tags={threadToLink.tags || []}
                    onUpdateSpaces={(spaceIds) => {
                        updateSpacesMutation.mutate({threadId: threadToLink.id, spaceIds});
                    }}
                    onUpdateTopic={(topicId) => {
                        updateTopicMutation.mutate({threadId: threadToLink.id, topicId});
                    }}
                    onUpdateTags={(tags) => {
                        updateTagsMutation.mutate({threadId: threadToLink.id, tags});
                    }}
                />
            )}
        </>
    );
}
